import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  useTheme,
  alpha,
  Skeleton
} from '@mui/material';
import {
  Assessment as QuotaIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import MUIStatCard from './ui/MUIStatCard';

const QuotaManagementInterface = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  
  const [quotas, setQuotas] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [stats, setStats] = useState({
    totalQuotas: 0,
    activeQuotas: 0,
    userTypesManaged: 0,
    quotaUsage: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showQuotaForm, setShowQuotaForm] = useState(false);
  const [editingQuota, setEditingQuota] = useState(null);
  
  const [formData, setFormData] = useState({
    userType: 'student',
    resourceType: 'facility',
    resourceId: '',
    dailyLimit: '',
    weeklyLimit: '',
    monthlyLimit: '',
    isActive: true,
    description: ''
  });

  const userTypes = [
    { value: 'student', label: t('admin:quotas.userTypes.student'), description: t('admin:quotas.userTypes.studentDescription') },
    { value: 'technopolis_employee', label: t('admin:quotas.userTypes.technopolisEmployee'), description: t('admin:quotas.userTypes.technopolisEmployeeDescription') },
    { value: 'external', label: t('admin:quotas.userTypes.external'), description: t('admin:quotas.userTypes.externalDescription') }
  ];

  const resourceTypes = [
    { value: 'facility', label: t('admin:quotas.resourceTypes.facilityBooking') },
    { value: 'class', label: t('admin:quotas.resourceTypes.classBooking') },
    { value: 'global', label: t('admin:quotas.resourceTypes.globalBooking') }
  ];

  const fetchQuotaData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const [quotasRes, facilitiesRes, classTypesRes, statsRes] = await Promise.all([
        api.get('/quota-management'),
        api.get('/facilities/all'),
        api.get('/class-types'),
        api.get('/quota-management/stats')
      ]);
      
      setQuotas(quotasRes.data.data || []);
      setFacilities(facilitiesRes.data.data || []);
      setClassTypes(classTypesRes.data.data || []);
      setStats(statsRes.data.data || {
        totalQuotas: 0,
        activeQuotas: 0,
        userTypesManaged: 0,
        quotaUsage: 0
      });
      
    } catch (error) {
      setError(t('admin:quotas.errors.loadFailed'));
      console.error('Error fetching quota data:', error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchQuotaData();
    }
  }, [user, fetchQuotaData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      userType: 'student',
      resourceType: 'facility',
      resourceId: '',
      dailyLimit: '',
      weeklyLimit: '',
      monthlyLimit: '',
      isActive: true,
      description: ''
    });
    setEditingQuota(null);
  };

  const handleEdit = (quota) => {
    setFormData({
      userType: quota.userType,
      resourceType: quota.resourceType,
      resourceId: quota.resourceId || '',
      dailyLimit: quota.limits.daily || '',
      weeklyLimit: quota.limits.weekly || '',
      monthlyLimit: quota.limits.monthly || '',
      isActive: quota.isActive,
      description: quota.description || ''
    });
    setEditingQuota(quota);
    setShowQuotaForm(true);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');

      const quotaData = {
        userType: formData.userType,
        resourceType: formData.resourceType,
        resourceId: formData.resourceType === 'global' ? undefined : formData.resourceId,
        limits: {
          daily: formData.dailyLimit ? parseInt(formData.dailyLimit) : undefined,
          weekly: formData.weeklyLimit ? parseInt(formData.weeklyLimit) : undefined,
          monthly: formData.monthlyLimit ? parseInt(formData.monthlyLimit) : undefined
        },
        isActive: formData.isActive,
        description: formData.description
      };

      if (editingQuota) {
        await api.put(`/quota-management/${editingQuota._id}`, quotaData);
      } else {
        await api.post('/quota-management', quotaData);
      }

      await fetchQuotaData();
      setShowQuotaForm(false);
      resetForm();
      
    } catch (error) {
      setError(error.response?.data?.message || t('admin:quotas.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (quotaId) => {
    if (!window.confirm(t('admin:quotas.dialogs.deleteConfirm'))) {
      return;
    }

    try {
      await api.delete(`/quota-management/${quotaId}`);
      await fetchQuotaData();
    } catch (error) {
      setError(t('admin:quotas.errors.deleteFailed'));
    }
  };

  const toggleQuotaStatus = async (quota) => {
    try {
      await api.put(`/quota-management/${quota._id}`, {
        ...quota,
        isActive: !quota.isActive
      });
      await fetchQuotaData();
    } catch (error) {
      setError(t('admin:quotas.errors.updateStatusFailed'));
    }
  };

  const getResourceName = (quota) => {
    if (quota.resourceType === 'global') {
      return t('admin:quotas.resourceNames.allResources');
    }
    
    if (quota.resourceType === 'facility') {
      const facility = facilities.find(f => f._id === quota.resourceId);
      return facility ? facility.name : t('admin:quotas.resourceNames.unknownFacility');
    }
    
    if (quota.resourceType === 'class') {
      const classType = classTypes.find(c => c._id === quota.resourceId);
      return classType ? classType.name : t('admin:quotas.resourceNames.unknownClass');
    }
    
    return t('admin:quotas.resourceNames.unknownResource');
  };


  const formatLimits = (limits) => {
    const parts = [];
    if (limits.daily) parts.push(t('admin:quotas.limits.perDay', { count: limits.daily }));
    if (limits.weekly) parts.push(t('admin:quotas.limits.perWeek', { count: limits.weekly }));
    if (limits.monthly) parts.push(t('admin:quotas.limits.perMonth', { count: limits.monthly }));
    return parts.join(', ') || t('admin:quotas.limits.noLimits');
  };

  if (user?.role !== 'admin') {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">
          {t('admin:quotas.accessDenied')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        borderRadius: 3,
        p: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QuotaIcon sx={{ fontSize: 48, mr: 2, color: 'warning.main' }} />
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: 'warning.main' }}>
                {t('admin:quotas.title')}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                {t('admin:quotas.subtitle')}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setShowQuotaForm(true);
            }}
            sx={{ borderRadius: 2 }}
          >
{t('admin:quotas.actions.addQuotaRule')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MUIStatCard
            title={t('admin:quotas.stats.totalQuotas')}
            value={stats.totalQuotas}
            icon={<QuotaIcon />}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MUIStatCard
            title={t('admin:quotas.stats.activeQuotas')}
            value={stats.activeQuotas}
            icon={<SettingsIcon />}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MUIStatCard
            title={t('admin:quotas.stats.userTypes')}
            value={stats.userTypesManaged}
            icon={<GroupIcon />}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MUIStatCard
            title={t('admin:quotas.stats.usageRate')}
            value={`${stats.quotaUsage}%`}
            icon={<TrendingUpIcon />}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Quota Rules by User Type */}
      <Grid container spacing={3}>
        {userTypes.map((userType) => {
          const userQuotas = quotas.filter(q => q.userType === userType.value);
          
          return (
            <Grid size={12} key={userType.value}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {userType.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {userType.description}
                      </Typography>
                    </Box>
                    <Chip 
                      label={t('admin:quotas.stats.rulesCount', { count: userQuotas.length })}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  
                  {loading ? (
                    <Box>
                      {[1, 2, 3].map(i => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 2, borderBottom: 1, borderColor: 'divider' }}>
                          <Skeleton width="30%" height={20} sx={{ mr: 2 }} />
                          <Skeleton width="20%" height={16} sx={{ mr: 2 }} />
                          <Skeleton width="25%" height={16} sx={{ mr: 2 }} />
                          <Skeleton width={60} height={24} sx={{ mr: 2 }} />
                          <Skeleton variant="circular" width={24} height={24} />
                        </Box>
                      ))}
                    </Box>
                  ) : userQuotas.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('admin:quotas.table.resource')}</TableCell>
                            <TableCell>{t('admin:quotas.table.type')}</TableCell>
                            <TableCell>{t('admin:quotas.table.limits')}</TableCell>
                            <TableCell>{t('admin:quotas.table.status')}</TableCell>
                            <TableCell>{t('admin:quotas.table.description')}</TableCell>
                            <TableCell align="right">{t('admin:quotas.table.actions')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userQuotas.map((quota) => (
                            <TableRow key={quota._id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {getResourceName(quota)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={quota.resourceType}
                                  size="small"
                                  variant="outlined"
                                  sx={{ textTransform: 'capitalize' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatLimits(quota.limits)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={quota.isActive ? t('admin:quotas.status.active') : t('admin:quotas.status.inactive')}
                                  color={quota.isActive ? 'success' : 'default'}
                                  size="small"
                                  onClick={() => toggleQuotaStatus(quota)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {quota.description || t('admin:quotas.table.noDescription')}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1}>
                                  <IconButton 
                                    size="small"
                                    onClick={() => handleEdit(quota)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton 
                                    size="small"
                                    color="error"
                                    onClick={() => handleDelete(quota._id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box textAlign="center" py={4}>
                      <QuotaIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="body1" color="text.secondary" gutterBottom>
                        {t('admin:quotas.emptyState.noRulesFor', { userType: userType.label.toLowerCase() })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('admin:quotas.emptyState.addRulesPrompt')}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Quota Form Dialog */}
      <Dialog 
        open={showQuotaForm} 
        onClose={() => setShowQuotaForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingQuota ? t('admin:quotas.dialogs.editQuotaRule') : t('admin:quotas.dialogs.addNewQuotaRule')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>{t('admin:quotas.form.userType')}</InputLabel>
                <Select
                  value={formData.userType}
                  label={t('admin:quotas.form.userType')}
                  onChange={(e) => handleInputChange('userType', e.target.value)}
                >
                  {userTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>{t('admin:quotas.form.resourceType')}</InputLabel>
                <Select
                  value={formData.resourceType}
                  label={t('admin:quotas.form.resourceType')}
                  onChange={(e) => {
                    handleInputChange('resourceType', e.target.value);
                    handleInputChange('resourceId', '');
                  }}
                >
                  {resourceTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {formData.resourceType !== 'global' && (
              <Grid size={12}>
                <FormControl fullWidth required>
                  <InputLabel>
                    {formData.resourceType === 'facility' ? t('admin:quotas.form.facility') : t('admin:quotas.form.classType')}
                  </InputLabel>
                  <Select
                    value={formData.resourceId}
                    label={formData.resourceType === 'facility' ? t('admin:quotas.form.facility') : t('admin:quotas.form.classType')}
                    onChange={(e) => handleInputChange('resourceId', e.target.value)}
                  >
                    {formData.resourceType === 'facility' 
                      ? facilities.map(facility => (
                          <MenuItem key={facility._id} value={facility._id}>
                            {facility.name}
                          </MenuItem>
                        ))
                      : classTypes.map(classType => (
                          <MenuItem key={classType._id} value={classType._id}>
                            {classType.name}
                          </MenuItem>
                        ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid size={4}>
              <TextField
                fullWidth
                label={t('admin:quotas.form.dailyLimit')}
                type="number"
                value={formData.dailyLimit}
                onChange={(e) => handleInputChange('dailyLimit', e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid size={4}>
              <TextField
                fullWidth
                label={t('admin:quotas.form.weeklyLimit')}
                type="number"
                value={formData.weeklyLimit}
                onChange={(e) => handleInputChange('weeklyLimit', e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid size={4}>
              <TextField
                fullWidth
                label={t('admin:quotas.form.monthlyLimit')}
                type="number"
                value={formData.monthlyLimit}
                onChange={(e) => handleInputChange('monthlyLimit', e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('admin:quotas.form.description')}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('admin:quotas.form.descriptionPlaceholder')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowQuotaForm(false)}
            disabled={saving}
          >
{t('admin:quotas.actions.cancel')}
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={saving}
            startIcon={<SaveIcon />}
          >
            {saving ? t('admin:quotas.actions.saving') : (editingQuota ? t('admin:quotas.actions.update') : t('admin:quotas.actions.create'))}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QuotaManagementInterface;